import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { CategoriaDeduccion, Deduccion, FacturaDetalle } from '@manejo-alcanza/contracts';
import {
  detalleFactura,
  enlazarOrdenes,
  fecha,
  listarTiposDeduccion,
  pesos,
  reclamarDeduccion,
  registrarDeduccion,
} from '../api';
import { Aviso, Boton, Card, Cargando, Error_, Tabla } from '../componentes/basicos';

/**
 * El puente factura → pago.
 *
 * **Es la pantalla que originó el proyecto.** Siigo sabe qué se facturó, el
 * banco sabe qué entró, y hasta ahora nadie podía explicar la diferencia. Aquí
 * se explica peso por peso — y lo que no se explica también se ve, que es lo
 * importante.
 */
export function DetalleFactura(): ReactNode {
  const { id = '' } = useParams();
  const consulta = useQuery({ queryKey: ['factura', id], queryFn: () => detalleFactura(id) });

  if (consulta.isLoading) return <Cargando que="la factura" />;
  if (consulta.isError) return <Error_>{(consulta.error as Error).message}</Error_>;

  const f = consulta.data!;

  return (
    <div className="space-y-5">
      <header>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold">{f.numero}</h1>
          {f.estado === 'ANULADA' && (
            <span className="rounded-full bg-neutro-tenue px-2 py-0.5 text-[0.7rem] font-semibold text-neutro">
              ANULADA
            </span>
          )}
        </div>
        <p className="mt-1 text-tenue">
          {f.clienteNombre} · emitida el {fecha(f.fechaEmision)}
          {f.registradaPor && ` · la registró ${f.registradaPor}`}
        </p>
      </header>

      {f.estado === 'ANULADA' && <Aviso>Factura anulada: {f.anuladaMotivo}</Aviso>}

      {/*
        Lo primero que hay que ver si pasa, por eso va arriba y no dentro de
        una tarjeta: una factura anulada ante la DIAN que aqui sigue contando
        en cartera es plata que no existe.
      */}
      {f.anuladaEnSiigoEn !== null && f.estado !== 'ANULADA' && (
        <Aviso>
          <strong>En Siigo esta anulada y aqui sigue vigente.</strong> Lo vimos el{' '}
          {new Date(f.anuladaEnSiigoEn).toLocaleDateString('es-CO')}. Siigo es la verdad
          fiscal, pero aqui no se cambia nada solo: mientras siga asi, esta factura cuenta
          en la cartera y ante la DIAN no existe. Si es correcto, anulala aqui tambien con
          su motivo.
        </Aviso>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Puente factura={f} />

        <div className="space-y-4">
          <Card titulo="Cuándo se espera el pago">
            {f.fechaEsperadaPago === null ? (
              <p className="text-sm text-tenue">
                <strong>No se sabe.</strong> Falta el día en que el cliente recibió la factura, y
                el plazo corre desde ahí — no desde que se emitió. Sin esa fecha, decir un día
                sería inventarlo.
              </p>
            ) : (
              <>
                <div className="cifra !text-left text-xl font-semibold">
                  {fecha(f.fechaEsperadaPago)}
                </div>
                <p className="mt-1 text-xs text-tenue">
                  {f.plazoPagoDias} días desde que la recibieron el{' '}
                  {f.fechaRecepcionCliente ? fecha(f.fechaRecepcionCliente) : '—'}
                </p>
                {f.diasVencida !== null && (
                  <p className="mt-2 rounded-md border border-alerta/30 bg-alerta-tenue px-3 py-2 text-sm text-alerta">
                    <strong>{f.diasVencida} días de mora.</strong> Quedan{' '}
                    {pesos(f.noExplicada)} por cobrar.
                  </p>
                )}
              </>
            )}
          </Card>

          <FacturadoContraRecibido factura={f} />
          <SaldoSegunSiigo factura={f} />
        </div>
      </div>

      <LineasDeLaFactura factura={f} />

      <Deducciones factura={f} />

      {f.recaudos.length > 0 && (
        <Card titulo={`Pagos aplicados (${f.recaudos.length})`}>
          <ul className="space-y-1 text-sm">
            {f.recaudos.map((r) => (
              <li key={r.recaudoId} className="flex flex-wrap items-baseline justify-between gap-2">
                <span>
                  {fecha(r.fechaRecaudo)} · {r.medio.toLowerCase()}
                  {r.referenciaSoporte && (
                    <span className="ml-2 font-mono text-xs text-tenue">
                      {r.referenciaSoporte}
                    </span>
                  )}
                </span>
                <span className="cifra font-medium">{pesos(r.valorAplicado)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * El puente, con la forma que tiene en el papel.
 *
 * Lo fiscal y lo comercial van **separados y con su nota**, porque es la
 * distinción que más se equivoca: la retención sale del pago pero vuelve en la
 * declaración; el rappel no vuelve nunca. Sumadas en una sola bolsa de
 * «descuentos», el margen parece peor de lo que es.
 */
function Puente({ factura }: { factura: FacturaDetalle }): ReactNode {
  const p = factura.puente;
  const cero = (v: string): boolean => Number(v) === 0;

  return (
    <Card titulo="De la factura al pago" className="lg:col-span-2">
      <dl className="space-y-1 text-sm">
        <Fila etiqueta="Facturado" valor={pesos(p.facturado)} fuerte />

        {!cero(p.porCategoria.FISCAL) && (
          <Fila
            etiqueta="Retenciones"
            nota="fiscal — vuelve en la declaración de renta, no es menor ingreso"
            valor={`− ${pesos(p.porCategoria.FISCAL)}`}
          />
        )}
        {!cero(p.porCategoria.COMERCIAL) && (
          <Fila
            etiqueta="Descuentos comerciales"
            nota="pactados — no vuelven nunca, solo se evitan renegociando"
            valor={`− ${pesos(p.porCategoria.COMERCIAL)}`}
          />
        )}
        {!cero(p.porCategoria.NOTA_DEBITO) && (
          <Fila
            etiqueta="Notas débito"
            nota="evitables, y reclamables — aquí está el valor"
            valor={`− ${pesos(p.porCategoria.NOTA_DEBITO)}`}
            alerta
          />
        )}
        {!cero(p.porCategoria.NO_EXPLICADA) && (
          <Fila
            etiqueta="Descuentos sin clasificar"
            nota="registrados, pero nadie ha dicho todavía de qué son"
            valor={`− ${pesos(p.porCategoria.NO_EXPLICADA)}`}
            alerta
          />
        )}

        <div className="border-t border-borde pt-1">
          <Fila etiqueta="Debería entrar" valor={pesos(p.esperado)} fuerte />
        </div>
        <Fila etiqueta="Entró al banco" valor={pesos(p.recaudado)} />

        <div className="border-t-2 border-borde pt-2">
          <Fila
            etiqueta={Number(p.noExplicada) < 0 ? 'Pagaron de más' : 'Sin explicar'}
            nota={
              Number(p.noExplicada) === 0
                ? 'todo cuadra'
                : Number(p.noExplicada) < 0
                  ? 'hoy es plata a favor; el mes que viene es un descuento que nadie va a saber de dónde salió'
                  : 'ninguna deducción registrada justifica esta diferencia'
            }
            valor={pesos(p.noExplicada)}
            fuerte
            alerta={Number(p.noExplicada) !== 0}
          />
        </div>
      </dl>

      {Number(p.noExplicada) === 0 && Number(p.recaudado) === 0 && (
        <p className="mt-3 text-xs text-tenue">
          Todavía no ha entrado nada ni se ha registrado ningún descuento. El «sin explicar»
          sale en cuanto haya un pago que no cuadre.
        </p>
      )}
    </Card>
  );
}

/**
 * Lo que Siigo cree que falta, contra lo que creemos nosotros.
 *
 * **Son dos opiniones sobre el mismo número**, y hasta ahora solo se miraba una.
 * Si no coinciden, alguna de las dos está mal: o hay un pago registrado allá que
 * no llegó aquí, o una deducción registrada aquí que allá no. Cualquiera de las
 * dos hay que mirarla antes de que la mire el cliente.
 *
 * El saldo de Siigo **no entra en ningún cálculo nuestro**. El puente sale de
 * nuestros propios hechos; esto solo se compara.
 */
function SaldoSegunSiigo({ factura }: { factura: FacturaDetalle }): ReactNode {
  if (factura.saldoSiigo === null) return null;

  const nuestro = Number(factura.puente.noExplicada);
  const suyo = Number(factura.saldoSiigo);
  const cuadra = Math.abs(nuestro - suyo) < 0.01;

  return (
    <Card titulo="Lo que dice Siigo">
      <dl className="space-y-1 text-sm">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-tenue">Pendiente según Siigo</dt>
          <dd className="cifra">{pesos(factura.saldoSiigo)}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-tenue">Pendiente según nosotros</dt>
          <dd className="cifra">{pesos(factura.puente.noExplicada)}</dd>
        </div>
      </dl>

      {cuadra ? (
        <p className="mt-2 text-xs text-tenue">
          Los dos dicen lo mismo.
          {factura.saldoSiigoEn &&
            ` Traído de Siigo el ${new Date(factura.saldoSiigoEn).toLocaleDateString('es-CO')}.`}
        </p>
      ) : (
        <div className="mt-2 rounded-md border border-aviso/30 bg-aviso-tenue px-3 py-2 text-xs text-aviso">
          <strong>No coinciden.</strong> O hay un pago registrado en Siigo que no llegó aquí,
          o una deducción registrada aquí que allá no.
          {factura.saldoSiigoEn &&
            ` El dato de Siigo es del ${new Date(factura.saldoSiigoEn).toLocaleDateString('es-CO')}.`}
        </div>
      )}
    </Card>
  );
}

/** La comparación de F6: se facturó esto, y el cliente había aceptado aquello. */
function FacturadoContraRecibido({ factura }: { factura: FacturaDetalle }): ReactNode {
  if (factura.ordenes.length === 0) {
    return (
      <Card titulo="Órdenes que cubre">
        <p className="text-sm text-tenue">
          Esta factura no está enlazada con ninguna orden. Mientras siga así no se puede
          comparar lo facturado contra lo que el cliente recibió, que es justamente para lo que
          existe este registro.
        </p>
        <ProponerOrden factura={factura} />
      </Card>
    );
  }

  return (
    <Card titulo="Órdenes que cubre">
      <ul className="space-y-3 text-sm">
        {factura.ordenes.map((o) => {
          const descuadra =
            o.valorRecibido !== null && Number(o.valorRecibido) !== Number(o.valorImputado);
          return (
            <li key={o.ordenId}>
              <Link to={`/ordenes/${o.ordenId}`} className="font-medium text-acento hover:underline">
                {o.numero}
              </Link>
              <dl className="mt-1 space-y-0.5 text-xs">
                <div className="flex justify-between gap-3">
                  <dt className="text-tenue">Facturado</dt>
                  <dd className="cifra">{pesos(o.valorImputado)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-tenue">Recibió el cliente</dt>
                  <dd className={`cifra ${descuadra ? 'text-alerta' : ''}`}>
                    {o.valorRecibido === null ? (
                      <span className="italic text-tenue">sin registrar</span>
                    ) : (
                      pesos(o.valorRecibido)
                    )}
                  </dd>
                </div>
              </dl>
              {descuadra && (
                <p className="mt-1 text-xs text-alerta">
                  Se facturó algo distinto de lo que aceptaron. Míralo antes de que llegue la
                  nota débito.
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------------------

/**
 * Lo que Siigo facturo, linea por linea, contra lo que el cliente recibio.
 *
 * **Dos totales que cuadran pueden esconder que se facturo un producto por
 * otro**, y una deduccion del cliente casi siempre habla de un producto
 * concreto: «me llegaron 28 Feta y me cobraron 30». Con solo el total esa
 * conversacion no se puede tener con datos.
 */
function LineasDeLaFactura({ factura }: { factura: FacturaDetalle }): ReactNode {
  if (factura.lineas.length === 0) return null;

  const c = factura.contraste;

  return (
    <Card titulo={`Lo que se facturo (${factura.lineas.length})`}>
      <Tabla>
        <thead>
          <tr>
            <th>Producto</th>
            <th className="text-right">Cantidad</th>
            <th className="text-right">Precio</th>
            <th className="text-right">Valor</th>
          </tr>
        </thead>
        <tbody>
          {factura.lineas.map((l) => (
            <tr key={l.numeroLinea}>
              <td>
                {l.presentacion ?? (
                  <>
                    {/* El dato crudo y la palabra «no identificada», nunca un hueco. */}
                    <span>{l.descripcion}</span>{' '}
                    <span className="text-xs text-tenue">
                      (codigo {l.codigoSiigo ?? "sin codigo"} de Siigo, sin identificar)
                    </span>
                  </>
                )}
              </td>
              <td className="cifra text-right">{l.cantidad}</td>
              <td className="cifra text-right">{pesos(l.precioUnitario)}</td>
              <td className="cifra text-right">{pesos(l.valorLinea)}</td>
            </tr>
          ))}
        </tbody>
      </Tabla>

      {c === null ? (
        <p className="mt-3 text-xs text-tenue">
          {factura.ordenes.length === 0
            ? 'Sin orden enlazada no hay contra que compararlas.'
            : 'Esta factura cubre varias ordenes, y repartir cada producto entre ellas ' +
              'seria una suposicion. El contraste por producto solo se ensena cuando ' +
              'hay una sola.'}
        </p>
      ) : (
        <div className="mt-4 border-t border-borde pt-3">
          <h3 className="mb-2 text-sm font-semibold">
            {c.hayDiferencias ? 'Facturado contra recibido' : 'Cuadra con lo recibido'}
          </h3>
          <ul className="space-y-1 text-sm">
            {c.filas.map((fila) => (
              <li key={fila.producto} className="flex flex-wrap justify-between gap-2">
                <span>
                  {fila.producto}
                  {fila.reparo !== null && (
                    <span className="ml-2 text-alerta">{fila.reparo}</span>
                  )}
                </span>
                <span className="cifra text-tenue">
                  facturado {fila.facturada ?? '—'} · recibido{' '}
                  {fila.recibida ?? 'no se sabe'}
                </span>
              </li>
            ))}
          </ul>
          {c.sinIdentificar.length > 0 && (
            <p className="mt-2 text-xs text-tenue">
              {c.sinIdentificar.length}{' '}
              {c.sinIdentificar.length === 1 ? 'linea no se pudo' : 'lineas no se pudieron'}{' '}
              cruzar porque su codigo de Siigo no esta en el maestro. No cuentan como
              faltante: simplemente no hay con que compararlas.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * La salida del callejon.
 *
 * Esta pantalla decia «no esta enlazada y por eso no se puede comparar» y no
 * ofrecia nada: el boton vivia en otra pestana y desde aqui no se veia.
 */
function ProponerOrden({ factura }: { factura: FacturaDetalle }): ReactNode {
  const cliente = useQueryClient();
  const s = factura.sugerencia;

  const enlazar = useMutation({
    mutationFn: (ordenId: string) =>
      enlazarOrdenes(factura.id, [{ ordenId, valorImputado: factura.valorTotal }]),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['factura', factura.id] });
      void cliente.invalidateQueries({ queryKey: ['conciliacion'] });
    },
  });

  if (s === null) return null;

  return (
    <div className="mt-3 border-t border-borde pt-3 text-sm">
      {factura.observaciones !== null && (
        <p className="mb-2 text-xs text-tenue">
          Siigo dice: <span className="font-mono">«{factura.observaciones}»</span>
        </p>
      )}

      {(s.clase === 'ENLAZAR' || s.clase === 'PROPONER') && (
        <>
          <p>
            {s.clase === 'ENLAZAR' ? (
              <>
                <span className="text-exito">✓</span> Parece la orden{' '}
              </>
            ) : (
              'Podria ser la orden '
            )}
            <Link to={`/ordenes/${s.orden.ordenId}`} className="text-acento hover:underline">
              {s.orden.numero}
            </Link>
            , entrega {s.orden.fechaEntregaMax}.{' '}
            {s.clase === 'ENLAZAR' ? (
              <>El valor cuadra con lo {s.contra === 'RECIBIDO' ? 'recibido' : 'pedido'}.</>
            ) : (
              <span className="text-alerta">{s.reparo}</span>
            )}
          </p>
          <div className="mt-2">
            <Boton
              tipo={s.clase === 'ENLAZAR' ? 'principal' : 'secundario'}
              onClick={() => enlazar.mutate(s.orden.ordenId)}
              deshabilitado={enlazar.isPending}
            >
              {s.clase === 'ENLAZAR' ? 'Es esta' : 'Enlazar igual'}
            </Boton>
          </div>
        </>
      )}

      {s.clase === 'VARIAS' && (
        <>
          <p className="text-alerta">
            Hay {s.ordenes.length} ordenes con el numero {s.numero}. No se elige sola.
          </p>
          <ul className="mt-2 space-y-1">
            {s.ordenes.map((o) => (
              <li key={o.ordenId} className="flex flex-wrap items-center gap-2">
                <span>
                  {o.numero} · entrega {o.fechaEntregaMax} · {pesos(o.valorPedido)}
                </span>
                <Boton
                  tipo="secundario"
                  onClick={() => enlazar.mutate(o.ordenId)}
                  deshabilitado={enlazar.isPending}
                >
                  Es esta
                </Boton>
              </li>
            ))}
          </ul>
        </>
      )}

      {s.clase === 'SIN_ORDEN' && (
        <p className="text-tenue">
          Dice la orden {s.numero} y no hay ninguna de este cliente con ese numero. O falta
          cargarla, o el numero esta mal escrito en Siigo.
        </p>
      )}

      {s.clase === 'SIN_NUMERO' && (
        <p className="text-tenue">
          Las observaciones de Siigo no mencionan ninguna orden, asi que no hay de donde
          sacarla.{' '}
          <Link to="/plata?ver=conciliacion" className="text-acento hover:underline">
            Buscarla en Conciliacion
          </Link>
          .
        </p>
      )}

      {enlazar.isError && (
        <div className="mt-2">
          <Error_>{(enlazar.error as Error).message}</Error_>
        </div>
      )}
    </div>
  );
}

function Deducciones({ factura }: { factura: FacturaDetalle }): ReactNode {
  const [abierto, setAbierto] = useState(false);

  return (
    <Card titulo={`Descuentos del cliente (${factura.deducciones.length})`}>
      {factura.deducciones.length === 0 ? (
        <p className="text-sm text-tenue">
          Ninguno registrado. Cuando llegue el estado de cuenta, cada descuento se registra aquí
          con su categoría — y lo que quede sin justificar aparece arriba como «sin explicar».
        </p>
      ) : (
        <Tabla>
          <thead>
            <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
              <th className="px-3 py-2 font-medium">Qué decía el soporte</th>
              <th className="px-3 py-2 font-medium">Categoría</th>
              <th className="px-3 py-2 font-medium">Por qué orden</th>
              <th className="px-3 py-2 text-right font-medium">Valor</th>
              <th className="px-3 py-2 font-medium">Reclamo</th>
            </tr>
          </thead>
          <tbody>
            {factura.deducciones.map((d) => (
              <FilaDeduccion key={d.id} deduccion={d} facturaId={factura.id} />
            ))}
          </tbody>
        </Tabla>
      )}

      <div className="mt-3">
        <Boton tipo="secundario" onClick={() => setAbierto((v) => !v)}>
          {abierto ? 'Cancelar' : 'Registrar un descuento'}
        </Boton>
      </div>

      {abierto && <FormularioDeduccion facturaId={factura.id} alTerminar={() => setAbierto(false)} />}
    </Card>
  );
}

const NOMBRE_CATEGORIA: Record<CategoriaDeduccion, string> = {
  FISCAL: 'Retención',
  COMERCIAL: 'Comercial',
  NOTA_DEBITO: 'Nota débito',
  NO_EXPLICADA: 'Sin clasificar',
};

function FilaDeduccion({
  deduccion,
  facturaId,
}: {
  deduccion: Deduccion;
  facturaId: string;
}): ReactNode {
  const cliente = useQueryClient();
  const [nota, setNota] = useState('');
  const [abierto, setAbierto] = useState(false);

  const reclamar = useMutation({
    mutationFn: () => reclamarDeduccion(deduccion.id, nota),
    onSuccess: () => {
      setAbierto(false);
      void cliente.invalidateQueries({ queryKey: ['factura', facturaId] });
    },
  });

  return (
    <tr className="border-b border-borde last:border-0">
      <td className="px-3 py-2">
        {deduccion.tipoNombre ?? (
          <span className="font-mono text-xs">{deduccion.descripcionCruda ?? '—'}</span>
        )}
        {deduccion.tipoNombre === null && (
          /* El sistema dice «no sé» en vez de clasificar a ojo. El contador lo
             cataloga una sola vez y la próxima resuelve solo. */
          <span className="ml-2 text-xs italic text-tenue">sin catalogar</span>
        )}
        <div className="text-xs text-tenue">{fecha(deduccion.fecha)}</div>
      </td>
      <td className="px-3 py-2">
        <span
          className={`rounded-full px-2 py-0.5 text-[0.7rem] font-semibold ${
            deduccion.categoria === 'FISCAL'
              ? 'bg-acento-tenue text-acento'
              : deduccion.categoria === 'COMERCIAL'
                ? 'bg-neutro-tenue text-neutro'
                : 'bg-alerta-tenue text-alerta'
          }`}
        >
          {NOMBRE_CATEGORIA[deduccion.categoria]}
        </span>
      </td>
      <td className="px-3 py-2 text-xs">
        {deduccion.ordenNumero ? (
          <Link to={`/ordenes/${deduccion.ordenId}`} className="text-acento hover:underline">
            {deduccion.ordenNumero}
          </Link>
        ) : (
          <span className="text-tenue">—</span>
        )}
      </td>
      <td className="cifra px-3 py-2 font-medium">{pesos(deduccion.valor)}</td>
      <td className="px-3 py-2 text-xs">
        {deduccion.categoria === 'FISCAL' ? (
          <span className="text-tenue" title="Vuelve en la declaración de renta">
            no aplica
          </span>
        ) : deduccion.reclamada ? (
          <span className="text-acento" title={deduccion.notaReclamo ?? ''}>
            reclamada
          </span>
        ) : abierto ? (
          <span className="flex flex-wrap gap-1">
            <input
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Cómo se reclamó"
              className="w-40 rounded-md border border-borde px-2 py-1 text-xs"
            />
            <Boton onClick={() => reclamar.mutate()} deshabilitado={nota.trim().length < 4}>
              Guardar
            </Boton>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setAbierto(true)}
            className="text-acento underline hover:no-underline"
          >
            marcar reclamada
          </button>
        )}
      </td>
    </tr>
  );
}

function FormularioDeduccion({
  facturaId,
  alTerminar,
}: {
  facturaId: string;
  alTerminar: () => void;
}): ReactNode {
  const cliente = useQueryClient();
  const tipos = useQuery({ queryKey: ['tipos-deduccion'], queryFn: listarTiposDeduccion });

  const [tipoId, setTipoId] = useState('');
  const [categoria, setCategoria] = useState<CategoriaDeduccion>('NOTA_DEBITO');
  const [descripcion, setDescripcion] = useState('');
  const [valor, setValor] = useState('');
  const [cuando, setCuando] = useState(hoy());

  const guardar = useMutation({
    mutationFn: () =>
      registrarDeduccion({
        facturaId,
        ...(tipoId ? { tipoDeduccionId: tipoId } : { categoria }),
        ...(descripcion.trim() ? { descripcionCruda: descripcion.trim() } : {}),
        valor: valor.trim().replace(',', '.'),
        fecha: cuando,
      }),
    onSuccess: () => {
      alTerminar();
      void cliente.invalidateQueries({ queryKey: ['factura', facturaId] });
    },
  });

  const listo = /^\d+(\.\d+)?$/.test(valor.trim().replace(',', '.')) && Number(valor.replace(',', '.')) > 0;
  const catalogo = tipos.data ?? [];

  return (
    <div className="mt-4 space-y-3 border-t border-borde pt-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs text-tenue">Tipo del catálogo</span>
          <select
            value={tipoId}
            onChange={(e) => setTipoId(e.target.value)}
            className="w-full rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
          >
            <option value="">
              {catalogo.length === 0 ? 'el catálogo está vacío todavía' : 'sin catalogar'}
            </option>
            {catalogo
              .filter((t) => t.activo)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre} · {NOMBRE_CATEGORIA[t.categoria]}
                </option>
              ))}
          </select>
        </label>

        {tipoId === '' && (
          <label className="block">
            <span className="mb-1 block text-xs text-tenue">De qué categoría es</span>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value as CategoriaDeduccion)}
              className="w-full rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
            >
              <option value="FISCAL">Retención — vuelve en la declaración</option>
              <option value="COMERCIAL">Comercial — pactado, no vuelve</option>
              <option value="NOTA_DEBITO">Nota débito — evitable y reclamable</option>
              <option value="NO_EXPLICADA">No sé todavía</option>
            </select>
          </label>
        )}

        <label className="block sm:col-span-2">
          <span className="mb-1 block text-xs text-tenue">
            Qué dice el soporte, tal cual
          </span>
          <input
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="DCTO LOGISTICO CENTRALIZACION"
            className="w-full rounded-md border border-borde px-2 py-1.5 text-sm"
          />
          {/* Ese texto es la llave para reconocerlo la próxima vez. Vale más
              copiarlo literal que escribir algo que suene mejor. */}
          <span className="mt-1 block text-xs text-tenue">
            Cópialo literal: es lo que permite reconocerlo la próxima vez.
          </span>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs text-tenue">Valor descontado</span>
          <input
            inputMode="decimal"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            className="cifra w-full rounded-md border border-borde px-2 py-1.5 text-sm"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs text-tenue">Fecha</span>
          <input
            type="date"
            value={cuando}
            max={hoy()}
            onChange={(e) => setCuando(e.target.value)}
            className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
          />
        </label>
      </div>

      {guardar.isError && <Error_>{(guardar.error as Error).message}</Error_>}

      <div className="flex gap-2">
        <Boton onClick={() => guardar.mutate()} deshabilitado={!listo || guardar.isPending}>
          Registrar
        </Boton>
        <Boton tipo="secundario" onClick={alTerminar}>
          Cancelar
        </Boton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Fila({
  etiqueta,
  valor,
  nota,
  fuerte = false,
  alerta = false,
}: {
  etiqueta: string;
  valor: string;
  nota?: string;
  fuerte?: boolean;
  alerta?: boolean;
}): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-tenue">
        {etiqueta}
        {nota && <span className="block text-xs">{nota}</span>}
      </dt>
      <dd
        className={`cifra ${fuerte ? 'text-base font-semibold' : ''} ${alerta ? 'text-alerta' : ''}`}
      >
        {valor}
      </dd>
    </div>
  );
}

function hoy(): string {
  return new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
